import { BusinessCategory, OrganizationDemographics, UseCase } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { isDefined } from '../helpers/utils';

/**
 * Reusable Demographics Form Component
 * Collects business category, use cases, website, and team size information
 */
@customElement('demographics-form')
export class DemographicsForm extends LitElement {
    @property({ type: Array })
    selectedBusinessCategories: BusinessCategory[] = [];

    @property({ type: Array })
    selectedUseCases: UseCase[] = [];

    @property({ type: String })
    website: string = '';

    @property({ type: String })
    teamSize: '1' | '2-5' | '5-10' | '20+' | '' = '';

    @property({ type: Boolean })
    showLabels: boolean = true;

    @state()
    private showAllBusinessCategories: boolean = false;

    @state()
    private showAllUseCases: boolean = false;

    private readonly businessCategoryOptions = Object.values(BusinessCategory);
    private readonly useCaseOptions = Object.values(UseCase);

    getDemographics(): OrganizationDemographics {
        return {
            business_category: this.selectedBusinessCategories.length > 0 ? this.selectedBusinessCategories : [],
            use_cases: this.selectedUseCases.length > 0 ? this.selectedUseCases : [],
            website: this.website || undefined,
            team_size: this.teamSize || undefined,
        };
    }

    setDemographics(demographics: OrganizationDemographics) {
        if (isDefined(demographics.business_category) && demographics.business_category.length > 0) {
            this.selectedBusinessCategories = [...demographics.business_category];
        }
        if (isDefined(demographics.use_cases) && demographics.use_cases.length > 0) {
            this.selectedUseCases = [...demographics.use_cases];
        }
        if (isDefined(demographics.website)) {
            this.website = demographics.website;
        }
        if (isDefined(demographics.team_size)) {
            this.teamSize = demographics.team_size;
        }
    }

    private toggleBusinessCategory(category: BusinessCategory) {
        if (this.selectedBusinessCategories.includes(category)) {
            this.selectedBusinessCategories = this.selectedBusinessCategories.filter(c => c !== category);
        } else {
            this.selectedBusinessCategories = [...this.selectedBusinessCategories, category];
        }
        this.dispatchEvent(new CustomEvent('demographics-changed', {
            detail: this.getDemographics()
        }));
    }

    private toggleUseCase(useCase: UseCase) {
        if (this.selectedUseCases.includes(useCase)) {
            this.selectedUseCases = this.selectedUseCases.filter(u => u !== useCase);
        } else {
            this.selectedUseCases = [...this.selectedUseCases, useCase];
        }
        this.dispatchEvent(new CustomEvent('demographics-changed', {
            detail: this.getDemographics()
        }));
    }

    private handleWebsiteChange(e: any) {
        this.website = e.target.value;
        this.dispatchEvent(new CustomEvent('demographics-changed', {
            detail: this.getDemographics()
        }));
    }

    private handleTeamSizeChange(e: any) {
        this.teamSize = e.target.value;
        this.dispatchEvent(new CustomEvent('demographics-changed', {
            detail: this.getDemographics()
        }));
    }

    render() {
        // Sort options to put selected items first
        const sortedBusinessCategories = [
            ...this.businessCategoryOptions.filter(cat => this.selectedBusinessCategories.includes(cat)),
            ...this.businessCategoryOptions.filter(cat => !this.selectedBusinessCategories.includes(cat))
        ];
        
        const sortedUseCases = [
            ...this.useCaseOptions.filter(uc => this.selectedUseCases.includes(uc)),
            ...this.useCaseOptions.filter(uc => !this.selectedUseCases.includes(uc))
        ];
        
        const visibleBusinessCategories = this.showAllBusinessCategories
            ? sortedBusinessCategories
            : sortedBusinessCategories.slice(0, 6);
        
        const visibleUseCases = this.showAllUseCases
            ? sortedUseCases
            : sortedUseCases.slice(0, 6);
        return html`
            <div class="form-section">
                ${this.showLabels ? html`<label class="form-label">What business category best describes your workspace?</label>` : ''}
                <div class="use-cases-grid">
                    ${visibleBusinessCategories.map(category => html`
                        <button
                            class="use-case-pill ${this.selectedBusinessCategories.includes(category) ? 'selected' : ''}"
                            @click=${() => this.toggleBusinessCategory(category)}
                        >
                            ${category}
                        </button>
                    `)}
                </div>
                ${!this.showAllBusinessCategories && this.businessCategoryOptions.length > 6 ? html`
                    <button
                        class="more-options-button"
                        @click=${() => { this.showAllBusinessCategories = true; }}
                    >
                        + ${this.businessCategoryOptions.length - 6} More Options
                    </button>
                ` : ''}
            </div>

            <div class="form-section">
                ${this.showLabels ? html`<label class="form-label">What are your most common use cases?</label>` : ''}
                <div class="use-cases-grid">
                    ${visibleUseCases.map(useCase => html`
                        <button
                            class="use-case-pill ${this.selectedUseCases.includes(useCase) ? 'selected' : ''}"
                            @click=${() => this.toggleUseCase(useCase)}
                        >
                            ${useCase}
                        </button>
                    `)}
                </div>
                ${!this.showAllUseCases && this.useCaseOptions.length > 6 ? html`
                    <button
                        class="more-options-button"
                        @click=${() => { this.showAllUseCases = true; }}
                    >
                        + ${this.useCaseOptions.length - 6} More Options
                    </button>
                ` : ''}
            </div>

            <div class="form-section">
                ${this.showLabels ? html`<label class="form-label">Workspace Website</label>` : ''}
                <proxi-textfield
                    outlined
                    type="url"
                    label="https://example.com"
                    .value=${this.website}
                    @input=${this.handleWebsiteChange}
                >
                </proxi-textfield>
            </div>

            <div class="form-section">
                ${this.showLabels ? html`<label class="form-label">Team Size</label>` : ''}
                <proxi-selectfield
                    outlined
                    label="Select team size"
                    .value=${this.teamSize}
                    @change=${this.handleTeamSizeChange}
                >
                    <mwc-list-item value=""></mwc-list-item>
                    <mwc-list-item value="1">1</mwc-list-item>
                    <mwc-list-item value="2-5">2-5</mwc-list-item>
                    <mwc-list-item value="5-10">5-10</mwc-list-item>
                    <mwc-list-item value="20+">20+</mwc-list-item>
                </proxi-selectfield>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .form-section {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .form-label {
                font-size: 0.875rem;
                font-weight: 600;
                color: var(--main-primary-color);
            }

            .use-cases-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
                gap: 0.5rem;
                margin-top: 0.25rem;
            }

            .use-case-pill {
                padding: 0.5rem 0.75rem;
                border: 1px solid var(--theme-border-color);
                border-radius: 4px;
                font-size: 0.75rem;
                text-align: center;
                cursor: pointer;
                transition: border-color 0.2s ease, background-color 0.2s ease;
                background-color: var(--theme-white-color);
                color: var(--main-primary-color);
                line-height: 1rem;
                font-family: inherit;
            }

            .use-case-pill:hover {
                border-color: var(--main-primary-color);
            }

            .use-case-pill.selected {
                background-color: var(--main-success-color);
                color: var(--theme-white-color);
                border-color: var(--main-success-color);
            }

            .more-options-button {
                padding: 0.5rem 1rem;
                margin-top: 0.5rem;
                border: 1px dashed var(--main-primary-color);
                border-radius: 4px;
                background: transparent;
                color: var(--main-primary-color);
                font-size: 0.875rem;
                cursor: pointer;
                transition: all 0.2s ease;
                font-family: inherit;
                width: 100%;
            }

            .more-options-button:hover {
                background: var(--theme-light-background-color, #f0f9ff);
                border-style: solid;
            }

            @media (max-width: 550px) {
                .use-cases-grid {
                    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
                }
            }
        `;
    }
}
